import { ReactNode, useEffect, useRef, useState } from "react"
import { Head, Link } from "@inertiajs/react"
import AuthLayout from "../layouts/AuthLayout"
import {
  ArrowUpRight,
  Cloud,
  FileText,
  FolderTree,
  Link2,
  Mail,
  Mic,
  PlayCircle,
  Tag,
  Twitter,
} from "lucide-react"

/**
 * Public-facing marketing page served at the root path for logged-out
 * visitors. The goal of this page is to sell the Builder Methods Starter
 * Kit (the ability to build your own version of this app), with a
 * secondary "follow Brian on X" CTA. See _artifacts/marketing/ for the
 * strategy, copy, and design direction that produced this.
 */

const STARTER_KIT_URL = "https://buildermethods.com"
const X_URL = "https://x.com/casjam"
const BUILDER_BRIEFING_URL = "https://buildermethods.com"

const MARKETING_STYLES = `
  :root {
    --mkt-font-display: 'Sora', ui-sans-serif, system-ui, sans-serif;
    --mkt-font-body: 'DM Sans', ui-sans-serif, system-ui, sans-serif;
  }
  .mkt-page {
    font-family: var(--mkt-font-body);
    font-optical-sizing: auto;
  }
  .mkt-display {
    font-family: var(--mkt-font-display);
    letter-spacing: -0.035em;
    font-weight: 700;
  }
  .mkt-display-accent {
    color: var(--primary);
  }
  .mkt-mono {
    font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
    font-variant-numeric: tabular-nums;
  }
  .mkt-grain {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.35 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    background-size: 180px 180px;
    opacity: 0.06;
    mix-blend-mode: multiply;
  }
  .dark .mkt-grain {
    opacity: 0.12;
    mix-blend-mode: screen;
  }
  .mkt-hero-glow {
    position: absolute;
    left: 50%;
    top: -20%;
    transform: translateX(-50%);
    width: min(900px, 110vw);
    aspect-ratio: 1 / 1;
    background: radial-gradient(closest-side, color-mix(in oklch, var(--primary) 14%, transparent), transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
  .dark .mkt-hero-glow {
    background: radial-gradient(closest-side, color-mix(in oklch, var(--primary) 22%, transparent), transparent 70%);
  }
  @keyframes mkt-rise {
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .mkt-rise {
    animation: mkt-rise 900ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  .mkt-reveal {
    opacity: 0;
    transform: translateY(16px);
    transition: opacity 900ms cubic-bezier(0.16, 1, 0.3, 1), transform 900ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .mkt-reveal.is-visible {
    opacity: 1;
    transform: translateY(0);
  }
  .mkt-hairline {
    border-color: color-mix(in oklch, var(--foreground) 12%, transparent);
  }
  .dark .mkt-hairline {
    border-color: color-mix(in oklch, var(--foreground) 14%, transparent);
  }
  .mkt-card {
    border: 1px solid color-mix(in oklch, var(--foreground) 12%, transparent);
    background: color-mix(in oklch, var(--background) 92%, var(--foreground) 1%);
    transition: border-color 220ms ease, transform 220ms ease, background 220ms ease;
  }
  .dark .mkt-card {
    background: color-mix(in oklch, var(--background) 84%, var(--foreground) 4%);
    border-color: color-mix(in oklch, var(--foreground) 14%, transparent);
  }
  .mkt-card:hover {
    border-color: color-mix(in oklch, var(--primary) 55%, transparent);
    transform: translateY(-2px);
  }
  .mkt-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    background: var(--primary);
    color: var(--primary-foreground);
    padding: 1.05rem 1.6rem;
    border-radius: 0.6rem;
    font-weight: 600;
    font-size: 1.05rem;
    letter-spacing: -0.005em;
    transition: transform 180ms ease, box-shadow 220ms ease, background 180ms ease;
    box-shadow: 0 1px 0 0 color-mix(in oklch, var(--foreground) 10%, transparent);
  }
  .mkt-btn-primary:hover {
    background: color-mix(in oklch, var(--primary) 92%, black);
    box-shadow: 0 6px 24px -8px color-mix(in oklch, var(--primary) 55%, transparent);
  }
  .dark .mkt-btn-primary:hover {
    background: color-mix(in oklch, var(--primary) 94%, white);
  }
  .mkt-btn-primary:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 3px;
  }
  .mkt-link-accent {
    color: var(--primary);
    transition: opacity 160ms ease;
  }
  .mkt-link-accent:hover { opacity: 0.75; }
  .mkt-cta-band {
    background: color-mix(in oklch, var(--foreground) 97%, transparent);
    color: var(--background);
  }
  .dark .mkt-cta-band {
    background: color-mix(in oklch, var(--background) 60%, var(--foreground) 6%);
    color: var(--foreground);
  }
  .mkt-cta-band .mkt-btn-primary {
    background: var(--background);
    color: var(--foreground);
    box-shadow: 0 1px 0 0 color-mix(in oklch, var(--background) 20%, transparent);
  }
  .dark .mkt-cta-band .mkt-btn-primary {
    background: var(--primary);
    color: var(--primary-foreground);
  }
  .mkt-cta-band .mkt-btn-primary:hover {
    background: color-mix(in oklch, var(--background) 92%, black);
  }
  .dark .mkt-cta-band .mkt-btn-primary:hover {
    background: color-mix(in oklch, var(--primary) 94%, white);
  }
  .mkt-step-num {
    font-family: var(--mkt-font-display);
    color: var(--primary);
    letter-spacing: -0.05em;
    line-height: 0.9;
    font-weight: 700;
  }
  .mkt-scroll-smooth { scroll-behavior: smooth; }
  @media (prefers-reduced-motion: reduce) {
    .mkt-rise, .mkt-reveal { animation: none !important; transition: none !important; opacity: 1 !important; transform: none !important; }
  }
`

function useReveal() {
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("is-visible")
      return
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.classList.add("is-visible")
            obs.disconnect()
          }
        })
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.1 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return ref
}

function Reveal({ children, className = "" }: { children: ReactNode; className?: string }) {
  const ref = useReveal()
  return (
    <div ref={ref} className={`mkt-reveal ${className}`}>
      {children}
    </div>
  )
}

function SectionLabel({ number, text }: { number: string; text: string }) {
  return (
    <div className="mkt-mono text-[0.8rem] uppercase tracking-[0.18em] text-foreground/55 flex items-center gap-3">
      <span>{number}</span>
      <span className="h-px flex-1 max-w-[44px] bg-current opacity-40" />
      <span>{text}</span>
    </div>
  )
}

interface FeatureProps {
  icon: ReactNode
  title: string
  body: string
}

function FeatureCard({ icon, title, body }: FeatureProps) {
  return (
    <div className="mkt-card rounded-lg p-7 flex flex-col gap-4 h-full">
      <div className="h-10 w-10 rounded-md mkt-hairline border flex items-center justify-center text-primary">
        {icon}
      </div>
      <h3 className="text-[1.2rem] font-semibold tracking-tight text-foreground">{title}</h3>
      <p className="text-[1.025rem] leading-[1.65] text-foreground/70">{body}</p>
    </div>
  )
}

interface StepProps {
  number: string
  title: string
  body: string
}

function StepCard({ number, title, body }: StepProps) {
  return (
    <div className="mkt-card rounded-lg p-8 flex flex-col gap-5">
      <div className="mkt-step-num text-[3.75rem] sm:text-[4.25rem] font-semibold">{number}</div>
      <div className="space-y-3">
        <h3 className="text-[1.35rem] font-semibold tracking-tight text-foreground">{title}</h3>
        <p className="text-[1.075rem] leading-[1.65] text-foreground/70">{body}</p>
      </div>
    </div>
  )
}

export default function Marketing() {
  const [navOpen, setNavOpen] = useState(false)

  return (
    <>
      <Head>
        <title>Brain Down — Markdown capture for the agent era</title>
        <meta
          name="description"
          content="Capture voice notes, text, and files into tagged markdown that lives in your own Dropbox. Every file has a stable link your AI agents can open, read, and edit. Built with the Builder Methods Starter Kit."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Brain Down — Markdown capture for the agent era" />
        <meta
          property="og:description"
          content="Capture voice notes, text, and files into tagged markdown that lives in your own Dropbox. Every file has a stable link your AI agents can open, read, and edit. Built with the Builder Methods Starter Kit."
        />
        <meta property="og:site_name" content="Brain Down" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@casjam" />
        <meta name="twitter:title" content="Brain Down — Markdown capture for the agent era" />
        <meta
          name="twitter:description"
          content="Capture voice, text, and files into tagged markdown that lives in your own Dropbox. Every file has a stable link you and your AI agents can open."
        />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400..700&family=Sora:wght@400..800&display=swap"
        />
      </Head>

      <style dangerouslySetInnerHTML={{ __html: MARKETING_STYLES }} />

      <div className="mkt-grain" aria-hidden="true" />

      <main className="mkt-page relative min-h-screen bg-background text-foreground antialiased mkt-scroll-smooth overflow-x-hidden">
        {/* ================= NAV ================= */}
        <header className="relative z-10 border-b mkt-hairline">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-8 h-[72px] flex items-center justify-between">
            <a href="#top" className="mkt-mono text-[0.9rem] uppercase tracking-[0.2em] font-semibold text-foreground">
              Brain·Down
            </a>
            <nav className="hidden md:flex items-center gap-9 mkt-mono text-[0.8rem] uppercase tracking-[0.16em] text-foreground/65">
              <a href="#features" className="hover:text-foreground transition-colors">Features</a>
              <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
              <a href="#kit" className="hover:text-foreground transition-colors">Get the Starter Kit</a>
              <Link href="/session/new" className="hover:text-foreground transition-colors">
                Sign in
              </Link>
            </nav>
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={navOpen}
              onClick={() => setNavOpen((v) => !v)}
              className="md:hidden mkt-mono text-[0.8rem] uppercase tracking-[0.16em] text-foreground/80 px-3.5 py-2 rounded border mkt-hairline"
            >
              {navOpen ? "Close" : "Menu"}
            </button>
          </div>
          {navOpen && (
            <div className="md:hidden border-t mkt-hairline">
              <div className="max-w-[1100px] mx-auto px-6 py-5 flex flex-col gap-4 mkt-mono text-[0.86rem] uppercase tracking-[0.14em]">
                <a href="#features" onClick={() => setNavOpen(false)} className="text-foreground/80">Features</a>
                <a href="#how" onClick={() => setNavOpen(false)} className="text-foreground/80">How it works</a>
                <a href="#kit" onClick={() => setNavOpen(false)} className="text-foreground/80">Get the Starter Kit</a>
                <Link href="/session/new" className="text-foreground/80">Sign in</Link>
              </div>
            </div>
          )}
        </header>

        {/* ================= HERO ================= */}
        <section id="top" className="relative">
          <div className="mkt-hero-glow" aria-hidden="true" />
          <div className="relative max-w-[1100px] mx-auto px-6 sm:px-8 pt-20 pb-16 sm:pt-28 sm:pb-24">
            <div className="mkt-rise" style={{ animationDelay: "0ms" }}>
              <div className="mkt-mono text-[0.82rem] uppercase tracking-[0.18em] text-foreground/55">
                A tool built &amp; used by{" "}
                <a
                  href={X_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-foreground/80 hover:text-foreground underline decoration-foreground/25 decoration-1 underline-offset-[3px] hover:decoration-foreground/60 transition-colors"
                >
                  Brian Casel
                </a>
              </div>
            </div>
            <div className="mkt-rise mt-6 sm:mt-8" style={{ animationDelay: "120ms" }}>
              <h1 className="mkt-display text-[2.6rem] leading-[1.02] sm:text-[4.25rem] md:text-[5rem] lg:text-[5.5rem] text-foreground">
                The fastest way to capture, tag, and <span className="mkt-display-accent">file</span> a markdown note.
              </h1>
            </div>
            <div className="mkt-rise mt-8 max-w-[62ch]" style={{ animationDelay: "260ms" }}>
              <p className="text-[1.2rem] sm:text-[1.325rem] leading-[1.6] text-foreground/75">
                Markdown files have quietly become how humans and AI agents collaborate — specs,
                plans, drafts, research, <span className="mkt-mono text-[0.9em] text-foreground/85">CLAUDE.md</span>.
                Brain Down is the fast, mobile-friendly home for all of it: capture from voice or
                text, tag it on the way in, and save straight to your own Dropbox with a link you
                and your agents can both open.
              </p>
            </div>
            <div className="mkt-rise mt-10 flex flex-col sm:flex-row sm:items-center gap-5" style={{ animationDelay: "400ms" }}>
              <a href={STARTER_KIT_URL} className="mkt-btn-primary" target="_blank" rel="noreferrer">
                Get the Starter Kit
                <ArrowUpRight className="h-[18px] w-[18px]" />
              </a>
              <div className="text-[0.95rem] text-foreground/60 max-w-[40ch] leading-[1.55]">
                Everything you need to build your own version of this app — specs, designs, and AI-agent guides.
              </div>
            </div>
          </div>

          {/* ================= HERO VIDEO SLOT ================= */}
          <div className="relative max-w-[1100px] mx-auto px-6 sm:px-8 pb-24">
            <Reveal>
              <div className="mkt-card rounded-xl overflow-hidden">
                <div className="aspect-[16/9] relative flex items-center justify-center bg-gradient-to-b from-transparent to-[color-mix(in_oklch,var(--foreground)_4%,transparent)]">
                  <div className="flex flex-col items-center gap-4 text-foreground/50">
                    <PlayCircle className="h-14 w-14" strokeWidth={1.25} />
                    <div className="mkt-mono text-[0.78rem] uppercase tracking-[0.18em]">Demo video coming soon</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ================= PROBLEM ================= */}
        <section className="relative border-t mkt-hairline">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-8 py-24 sm:py-32">
            <Reveal>
              <SectionLabel number="01" text="The problem" />
            </Reveal>
            <Reveal className="mt-8">
              <h2 className="mkt-display text-[2.4rem] sm:text-[3.4rem] md:text-[4rem] leading-[1.03] text-foreground">
                Markdown is everywhere now. Your tools <span className="mkt-display-accent">aren&rsquo;t</span>.
              </h2>
            </Reveal>
            <Reveal className="mt-10 max-w-[60ch]">
              <p className="text-[1.2rem] sm:text-[1.3rem] leading-[1.7] text-foreground/75">
                Plans, specs, drafts, research, <span className="mkt-mono text-[0.9em]">CLAUDE.md</span>, agent
                artifacts — half your work is already in markdown files. The other half is trapped in apps that
                won&rsquo;t give you a file path you can share. So you end up pasting content into chats instead
                of sending a link, and your agents work from stale snippets instead of the source.
                Brain Down is what happens when you stop fighting it: one place to capture, tag, and open any
                markdown file, with a stable link you and your agents can hand back and forth.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ================= HOW IT WORKS ================= */}
        <section id="how" className="relative border-t mkt-hairline">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-8 py-24 sm:py-32">
            <Reveal>
              <SectionLabel number="02" text="How it works" />
            </Reveal>
            <Reveal className="mt-8">
              <h2 className="mkt-display text-[2.4rem] sm:text-[3.2rem] md:text-[3.75rem] leading-[1.05] text-foreground">
                From fleeting thought to filed markdown in four taps.
              </h2>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Reveal>
                <StepCard
                  number="01"
                  title="Capture."
                  body={`Open the app and you're already on the capture screen. Tap Voice to record (it transcribes automatically). Tap Text to start typing. Or drop in a file or image. No menu-diving, no "new note" button.`}
                />
              </Reveal>
              <Reveal>
                <StepCard
                  number="02"
                  title="Tag."
                  body="Front matter fields show up as big, tappable buttons — project, type, status, whatever you set. Tag as you save, not weeks later."
                />
              </Reveal>
              <Reveal>
                <StepCard
                  number="03"
                  title="Auto-route."
                  body="Your routing rules file each note to the right Dropbox folder based on the tags you just picked. No inbox. No cleanup pass."
                />
              </Reveal>
              <Reveal>
                <StepCard
                  number="04"
                  title="Share the link."
                  body="Every file has a stable URL. Paste it into your agent, your notes, or a teammate chat — they can open, read, and edit the same markdown you're looking at."
                />
              </Reveal>
            </div>
          </div>
        </section>

        {/* ================= FEATURES ================= */}
        <section id="features" className="relative border-t mkt-hairline">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-8 py-24 sm:py-32">
            <Reveal>
              <SectionLabel number="03" text="Features" />
            </Reveal>
            <Reveal className="mt-8">
              <h2 className="mkt-display text-[2.4rem] sm:text-[3.2rem] md:text-[3.75rem] leading-[1.05] text-foreground">
                Built for how you actually <span className="mkt-display-accent">work</span> with markdown.
              </h2>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <Reveal>
                <FeatureCard
                  icon={<FileText className="h-5 w-5" strokeWidth={1.75} />}
                  title="Milkdown editor, iOS-polished"
                  body="Read and edit markdown fast on any device. Floating toolbar, keyboard shortcuts, list-indent gestures, and a selection behavior that actually works on your phone."
                />
              </Reveal>
              <Reveal>
                <FeatureCard
                  icon={<Link2 className="h-5 w-5" strokeWidth={1.75} />}
                  title="Linkable files"
                  body="Every markdown file gets a stable URL. Share it with your agent, your teammate, or your future self — they'll open the same source, not a pasted copy."
                />
              </Reveal>
              <Reveal>
                <FeatureCard
                  icon={<Mic className="h-5 w-5" strokeWidth={1.75} />}
                  title="Voice → transcript → markdown"
                  body="Record a thought. Brain Down streams the audio to the cloud, transcribes it via AssemblyAI, and drops a tagged markdown file into the right folder while you're already onto the next thing."
                />
              </Reveal>
              <Reveal>
                <FeatureCard
                  icon={<Tag className="h-5 w-5" strokeWidth={1.75} />}
                  title="Front matter as buttons"
                  body="Define your own fields and options. During save, they're big tappable buttons — tagging is finally part of capture instead of a chore."
                />
              </Reveal>
              <Reveal>
                <FeatureCard
                  icon={<FolderTree className="h-5 w-5" strokeWidth={1.75} />}
                  title="Routing rules"
                  body={`"If project = client-a, save to /Clients/Client A/Notes." Set it once, stop filing manually forever.`}
                />
              </Reveal>
              <Reveal>
                <FeatureCard
                  icon={<Cloud className="h-5 w-5" strokeWidth={1.75} />}
                  title="Your files in your Dropbox"
                  body="Brain Down reads and writes your Dropbox directly. You own the markdown. Quit anytime, take everything with you, hand a path to any agent you like."
                />
              </Reveal>
            </div>

            <Reveal className="mt-14">
              <div className="mkt-mono text-[0.78rem] uppercase tracking-[0.18em] text-foreground/55 flex flex-wrap gap-x-5 gap-y-2 items-center border-t mkt-hairline pt-8">
                <span>Light / dark mode</span>
                <span aria-hidden="true">•</span>
                <span>13 color themes</span>
                <span aria-hidden="true">•</span>
                <span>PWA-ready</span>
                <span aria-hidden="true">•</span>
                <span>Full-text search</span>
                <span aria-hidden="true">•</span>
                <span>Favorites</span>
                <span aria-hidden="true">•</span>
                <span>Intake API</span>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ================= STARTER KIT / BOTTOM CTA (merged) ================= */}
        <section id="kit" className="relative border-t mkt-hairline">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-8 py-24 sm:py-28">
            <Reveal>
              <SectionLabel number="04" text="Build your own" />
            </Reveal>
            <Reveal className="mt-10">
              <div className="mkt-cta-band rounded-2xl px-6 sm:px-12 md:px-16 py-14 sm:py-16">
                <h2 className="mkt-display text-[2.6rem] sm:text-[3.6rem] md:text-[4.25rem] leading-[1.02]">
                  Build your own version of <span className="mkt-display-accent">Brain Down</span>.
                </h2>
                <p className="mt-7 max-w-[62ch] text-[1.2rem] sm:text-[1.3rem] leading-[1.6] opacity-80">
                  Brain Down isn&rsquo;t a product I&rsquo;m trying to turn into a SaaS. It&rsquo;s one of
                  the builds inside the Builder Methods Starter Kit — grab it, open your coding agent, and
                  have your own version running this week. Rip out the parts you don&rsquo;t want, bolt on
                  the parts you do. It&rsquo;s your app — I just built the template.
                </p>

                <div className="mt-10 mkt-mono text-[0.78rem] uppercase tracking-[0.18em] opacity-60">
                  What&rsquo;s in the kit
                </div>
                <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
                  {[
                    "A complete product overview and implementation plan",
                    "Design system tokens, shell, and every section pre-designed",
                    "Section-by-section prompts for your coding agent",
                    "Data shapes, component contracts, and test specs",
                    "One-shot and incremental build modes",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <span className="mkt-mono mt-[0.4em] text-[0.78rem] tabular-nums opacity-55">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[1.1rem] sm:text-[1.15rem] leading-[1.55] opacity-90">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-12">
                  <a href={STARTER_KIT_URL} target="_blank" rel="noreferrer" className="mkt-btn-primary">
                    Get the Starter Kit
                    <ArrowUpRight className="h-[18px] w-[18px]" />
                  </a>
                </div>
              </div>
            </Reveal>

            {/* Secondary CTAs — sit below the starter kit box, outside the container */}
            <Reveal className="mt-10">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-4 sm:gap-10">
                <a
                  href={X_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2.5 text-foreground/70 hover:text-foreground transition-colors text-[0.98rem]"
                >
                  <Twitter className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  <span>Follow Brian on X</span>
                </a>
                <a
                  href={BUILDER_BRIEFING_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2.5 text-foreground/70 hover:text-foreground transition-colors text-[0.98rem]"
                >
                  <Mail className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  <span>Subscribe to Builder Briefing</span>
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ================= FOOTER ================= */}
        <footer className="relative border-t mkt-hairline">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-8 py-10 flex items-center justify-center">
            <div className="mkt-mono text-[0.78rem] uppercase tracking-[0.18em] text-foreground/55">
              © 2026 CasJam Media LLC
            </div>
          </div>
        </footer>
      </main>
    </>
  )
}

// Use the minimal auth layout (ThemeProvider only) so we don't inherit the
// authenticated AppShell / side nav / etc.
Marketing.layout = (page: ReactNode) => <AuthLayout>{page}</AuthLayout>
